import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { ConfigContext } from 'antd/lib/config-provider';
import { Tab } from './state';
import { MultitabRouter } from './Router';
import { OutletContext } from './context';
import { getPathList, getRouteList } from './share';
import { NavigateWithQuery } from 'hooks/useNavigateWithQuery';
import { useMemoizedFn } from 'ahooks';
import { usePermissionRouteConfig } from 'components/Router/context';

export interface PageProps {
  tab: Tab;
  active: boolean;
  elementsFilter?: (elements: JSX.Element[]) => JSX.Element[];
}

const Page = memo(({ tab, active, elementsFilter }: PageProps) => {
  const { path } = tab;
  const { mainRoutes } = usePermissionRouteConfig();
  const { getPopupContainer: getInheritedPopupContainer } = useContext(ConfigContext);
  const [section, setSection] = useState<HTMLElement | null>(null);
  const getPopupContainer = useCallback(
    (triggerNode?: HTMLElement) => {
      // Tooltip/Select portals must not inherit the tab's full-height child styles.
      // Modal has no trigger; Drawer uses body. Keep those overlays with their tab.
      if (triggerNode && triggerNode !== document.body) {
        return getInheritedPopupContainer?.(triggerNode) ?? document.body;
      }
      return section ?? document.body;
    },
    [section, getInheritedPopupContainer],
  );

  const memoElementsFilter = useMemoizedFn((elements: JSX.Element[]) =>
    elementsFilter ? elementsFilter(elements) : elements,
  );

  const { content, params, redirect } = useMemo(() => {
    const routeList = getRouteList(mainRoutes ?? [], path);

    const matchedPathList = routeList.reduce<string[]>((res, { path }) => {
      return [...res, ...path.split('/').filter((_) => _)];
    }, []);

    const params = matchedPathList.reduce<Record<string, string>>((res, cur, index) => {
      if (cur.startsWith(':')) {
        res[cur.replaceAll(':', '').replaceAll('?', '')] = getPathList(path)[index];
      }
      return res;
    }, {});

    const render = (elements: JSX.Element[]) => {
      if (elements.length === 0) {
        return <></>;
      }

      return (
        <OutletContext.Provider value={{ outlet: render(elements.slice(1)) }}>
          {elements[0]}
        </OutletContext.Provider>
      );
    };

    const content = render(
      memoElementsFilter(routeList.map((_) => _.element).filter((_) => _) as any),
    );

    const route = routeList[routeList.length - 1];
    const redirect =
      typeof route.redirect === 'string'
        ? route.redirect
        : route.redirect && route.children?.length
        ? // Page 手动渲染子路由，React Router 的相对基准仍可能是工作台父路由。
          // 默认子页必须基于当前 tab 的完整路径，避免 refund/return 丢失 refund。
          route.children[0].path.startsWith('/')
          ? route.children[0].path
          : `${path.replace(/\/$/, '')}/${route.children[0].path}`
        : '';

    return {
      params,
      content,
      redirect,
    };
  }, [path, mainRoutes, memoElementsFilter]);

  return (
    <section ref={setSection} data-agent-workspace-page={path} style={active ? undefined : { display: 'none' }}>
      {/* Modal chooses its portal target on mount, after the tab container exists. */}
      {section && (
        <ConfigProvider getPopupContainer={getPopupContainer}>
          <MultitabRouter tab={tab} params={params}>
            {content}
            {redirect ? <NavigateWithQuery replace key={redirect} to={redirect} /> : null}
          </MultitabRouter>
        </ConfigProvider>
      )}
    </section>
  );
});

export default Page;
